import React, { useEffect, useState } from 'react';
import { Coins, Star, Check, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useCredits, CreditPackage } from '@/hooks/useCredits';
import { fetchWallet } from '@/services/affiliateApi';

interface CreditPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { balance, packages, fetchPackages, purchaseCredits, loading } = useCredits();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  // Hey Joe credit (wallet dollars) pays first, same as event checkout.
  const [walletCents, setWalletCents] = useState(0);
  const [useWallet, setUseWallet] = useState(true);

  useEffect(() => {
    if (open) {
      fetchPackages();
      fetchWallet().then(w => setWalletCents(w?.balanceCents || 0)).catch(() => setWalletCents(0));
    }
  }, [open, fetchPackages]);

  // What the card pays for a pack after Hey Joe credit (Stripe can't take under 50 cents).
  const cardCents = (price: number) => {
    if (!useWallet || walletCents <= 0) return price;
    let apply = Math.min(walletCents, price);
    const rem = price - apply;
    if (rem > 0 && rem < 50) apply = Math.max(0, price - 50);
    return price - apply;
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handlePurchase = async (packageId: string) => {
    setPurchasing(true);
    setSelectedPackage(packageId);

    try {
      const checkoutUrl = await purchaseCredits(packageId, useWallet);
      if (checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = checkoutUrl;
      }
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        style={{
          background: 'var(--hj-surface-1)',
          border: '1px solid var(--hj-hairline)',
          color: 'var(--hj-ink)',
        }}
      >
        <DialogHeader style={{ gap: 8 }}>
          <div
            className="inline-flex items-center gap-2 mono-label self-start"
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--hj-yellow-tint)',
              color: 'var(--hj-yellow-ink)',
              fontWeight: 700,
            }}
          >
            <Coins className="h-3.5 w-3.5" />
            Video tokens
          </div>
          <DialogTitle asChild>
            <h2
              className="font-hj-display m-0"
              style={{
                fontSize: 28,
                color: 'var(--hj-ink)',
                letterSpacing: '-0.03em',
                fontWeight: 800,
                marginTop: 4,
              }}
            >
              Top up.
            </h2>
          </DialogTitle>
          <DialogDescription asChild>
            <p
              className="m-0"
              style={{ fontSize: 13.5, color: 'var(--hj-ink-3)', lineHeight: 1.5 }}
            >
              Video tokens power Video Enhancer features — smart editing, video gen, image gen.
            </p>
          </DialogDescription>
        </DialogHeader>

        {/* Current Balance */}
        {balance && (
          <div
            style={{
              padding: 14,
              borderRadius: 'var(--hj-r-sm)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginTop: 4,
            }}
          >
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Current balance</div>
            <div
              className="tabular"
              style={{
                marginTop: 4,
                fontSize: 26,
                fontWeight: 600,
                color: 'var(--hj-ink)',
                fontFamily: 'var(--hj-font-mono)',
                lineHeight: 1,
              }}
            >
              {balance.totalCredits} <span style={{ fontSize: 13, color: 'var(--hj-ink-3)', fontWeight: 400 }}>video tokens</span>
            </div>
          </div>
        )}

        {/* Package Options */}
        <div className="flex flex-col gap-2">
          {packages.map((pkg: CreditPackage) => (
            <div
              key={pkg.packageId}
              className="relative cursor-pointer transition-colors"
              onClick={() => !purchasing && handlePurchase(pkg.packageId)}
              style={{
                padding: 14,
                borderRadius: 'var(--hj-r-sm)',
                background: pkg.popular ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                border: `1px solid ${pkg.popular ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
              }}
            >
              {pkg.popular && (
                <div
                  className="mono-label absolute inline-flex items-center gap-1"
                  style={{
                    top: -10,
                    left: 14,
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: 'var(--hj-blue)',
                    color: 'oklch(0.18 0.005 70)',
                    fontWeight: 700,
                  }}
                >
                  <Star className="h-3 w-3" />
                  Most popular
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>{pkg.name}</div>
                  <div className="tabular" style={{ marginTop: 2, fontSize: 12, color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                    {pkg.credits} video tokens
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="tabular"
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: 'var(--hj-ink)',
                      fontFamily: 'var(--hj-font-mono)',
                    }}
                  >
                    {formatPrice(pkg.priceInCents)}
                  </div>
                  <div style={{ marginTop: 2, fontSize: 11, color: 'var(--hj-ink-2)' }}>
                    {cardCents(pkg.priceInCents) < pkg.priceInCents
                      ? (cardCents(pkg.priceInCents) === 0 ? 'Covered by your Hey Joe credit' : `You pay ${formatPrice(cardCents(pkg.priceInCents))}`)
                      : `${formatPrice(Math.round(pkg.priceInCents / pkg.credits))}/token`}
                  </div>
                </div>
              </div>

              {purchasing && selectedPackage === pkg.packageId && (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    background: 'oklch(0 0 0 / 0.6)',
                    borderRadius: 'var(--hj-r-sm)',
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--hj-ink)' }}>Redirecting…</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {walletCents > 0 && (
          <label className="flex items-start gap-3" style={{ padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', cursor: 'pointer' }}>
            <input type="checkbox" checked={useWallet} onChange={e => setUseWallet(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--hj-ink)' }} />
            <span style={{ fontSize: 13, color: 'var(--hj-ink)' }}>
              <strong>Use your Hey Joe credit</strong> ({formatPrice(walletCents)} available)
            </span>
          </label>
        )}

        {/* Info */}
        <div
          className="flex flex-col gap-1"
          style={{ marginTop: 4, fontSize: 11.5, color: 'var(--hj-ink-3)' }}
        >
          <p className="m-0 flex items-center gap-1.5">
            <Check className="h-3 w-3" style={{ color: 'var(--hj-green)' }} />
            Video tokens never expire
          </p>
          <p className="m-0 flex items-center gap-1.5">
            <ExternalLink className="h-3 w-3" />
            Secure Stripe checkout
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
